import { useEffect, useMemo, useState } from "react"
import { Alert, Button, Col, Container, Row, Table } from "react-bootstrap"
import { apiClient } from "../services/api/client"

const SCOPE_LABELS = {
  loginKey: "Login uživatel",
  resetKey: "Reset kombinace",
  loginIp: "Login IP",
  resetIp: "Reset IP",
}

const scopeOrder = ["loginKey", "resetKey", "loginIp", "resetIp"]

const formatRemaining = (remainingMs) => {
  const totalSec = Math.max(0, Math.ceil(Number(remainingMs || 0) / 1000))
  const minutes = Math.floor(totalSec / 60)
  const seconds = totalSec % 60
  return `${minutes}m ${seconds}s`
}

const Blocks = ({ tabActive }) => {
  const [blocked, setBlocked] = useState({})
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState(null)
  const [error, setError] = useState(null)

  const totalBlocked = useMemo(() => {
    return Object.values(blocked || {}).reduce((acc, rows) => acc + (Array.isArray(rows) ? rows.length : 0), 0)
  }, [blocked])

  const fetchBlocked = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await apiClient.get("/login/blokace")
      setBlocked(data?.blocked || {})
    } catch (requestError) {
      setError(requestError.message || "Nepodařilo se načíst blokace.")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!tabActive) return
    fetchBlocked()
  }, [tabActive])

  const handleUnblock = async (scope, key) => {
    setMessage(null)
    setError(null)
    try {
      const response = await apiClient.post("/login/blokace/odblokovat", { scope, key })
      setMessage(response?.message || "Blokace byla odstraněna.")
      fetchBlocked()
    } catch (requestError) {
      setError(requestError.message || "Nepodařilo se odblokovat záznam.")
    }
  }

  const handleUnblockAll = async () => {
    setMessage(null)
    setError(null)
    try {
      const response = await apiClient.post("/login/blokace/odblokovat", { scope: "all" })
      setMessage(response?.message || "Všechny blokace byly odstraněny.")
      fetchBlocked()
    } catch (requestError) {
      setError(requestError.message || "Nepodařilo se odblokovat všechny záznamy.")
    }
  }

  return (
    <Container fluid className="px-0">
      <Row>
        <Col lg={6}>
          <div className="d-flex justify-content-between align-items-center mb-4 mt-2">
            <div>
              <h5 className="mb-1">Blokace přihlášení a resetu</h5>
              <p className="text-muted small mb-0">Celkem aktivních blokací: {totalBlocked}</p>
            </div>
            <div className="d-flex gap-2">
              <Button variant="outline-secondary" onClick={fetchBlocked} disabled={loading}>
                Obnovit
              </Button>
              <Button variant="danger" onClick={handleUnblockAll} disabled={loading || totalBlocked === 0}>
                Odblokovat vše
              </Button>
            </div>
          </div>

          {message && (
            <Alert variant="success" className="rounded-3">
              {message}
            </Alert>
          )}
          {error && (
            <Alert variant="danger" className="rounded-3">
              {error}
            </Alert>
          )}

          <Row className="g-2">
            {scopeOrder.map((scope) => {
              const rows = blocked?.[scope] || []
              return (
                <Col key={scope} xs={12}>
                  <div className="border rounded-3 p-3 bg-body">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <strong>{SCOPE_LABELS[scope]}</strong>
                      <span className="text-muted small">{rows.length} blokací</span>
                    </div>
                    {rows.length === 0 ? (
                      <p className="text-muted small mb-0">Žádné aktivní blokace.</p>
                    ) : (
                      <Table responsive hover className="table-transparent mb-0">
                        <thead>
                          <tr>
                            <th>Klíč</th>
                            <th>Zbývá</th>
                            <th>Akce</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((row) => (
                            <tr key={`${scope}-${row.key}`}>
                              <td>{row.key}</td>
                              <td>{formatRemaining(row.remainingMs)}</td>
                              <td>
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() => handleUnblock(scope, row.key)}
                                  disabled={loading}
                                >
                                  Odblokovat
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </div>
                </Col>
              )
            })}
          </Row>
        </Col>
      </Row>
    </Container>
  )
}

export default Blocks
